import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDuration, fmtDateTime } from "@/lib/time";
import { FileText, Download, FileSpreadsheet, FileType, Trophy, Medal, TrendingUp, TrendingDown, Minus, Crown, Flame, Award } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";

function Sparkline({ data, endDate }) {
  const [hover, setHover] = useState(null);
  if (!data || data.every((v) => v == null)) return <span className="text-muted-foreground text-xs">—</span>;
  const pts = data.map((v) => (v == null ? 0 : v));
  const w = 84, h = 24, max = 100;
  const step = pts.length > 1 ? w / (pts.length - 1) : w;
  const coords = pts.map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * h).toFixed(1)}`).join(" ");
  const last = pts[pts.length - 1];
  const color = last >= 80 ? "#22C55E" : last >= 50 ? "#F59E0B" : "#FF3B30";
  const labelFor = (i) => {
    if (!endDate) return `Day ${i + 1}`;
    const d = new Date(endDate); d.setDate(d.getDate() - (data.length - 1 - i));
    return d.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short" });
  };
  return (
    <div className="relative inline-block" data-testid="sparkline">
      <svg width={w} height={h} className="block overflow-visible">
        <polyline points={coords} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
        {pts.map((v, i) => (
          <circle key={`c${i}`} cx={(i * step).toFixed(1)} cy={(h - (v / max) * h).toFixed(1)}
            r={hover === i ? 3.2 : (i === pts.length - 1 ? 2.2 : 1.6)} fill={color}
            style={{ opacity: data[i] == null ? 0.35 : 1, transition: "r 120ms ease" }} />
        ))}
        {pts.map((v, i) => (
          <rect key={`r${i}`} x={(i * step - step / 2).toFixed(1)} y={0} width={step} height={h}
            fill="transparent" className="cursor-pointer"
            onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />
        ))}
      </svg>
      {hover != null && (
        <div className="absolute z-30 bottom-full mb-1 left-1/2 -translate-x-1/2 whitespace-nowrap bg-[#121212] border border-[#262626] text-white text-[10px] px-2 py-1 rounded shadow-lg pointer-events-none" data-testid="spark-tooltip">
          <div className="font-mono-t text-muted-foreground">{labelFor(hover)}</div>
          <div className="font-semibold">{data[hover] == null ? "No data" : `${data[hover]}% on-target`}</div>
        </div>
      )}
    </div>
  );
}

function ExportButtons({ kind, storeId }) {
  const dl = async (fmt) => {
    try {
      const q = new URLSearchParams({ kind, fmt });
      if (storeId && storeId !== "all") q.set("store_id", storeId);
      const res = await api.get(`/reports/export?${q.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `trackboard_${kind}.${fmt === "excel" ? "xlsx" : fmt}`;
      a.click(); URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch { toast.error("Export failed"); }
  };
  return (
    <div className="flex gap-2">
      <Button size="sm" variant="outline" className="gap-1" onClick={() => dl("csv")} data-testid={`export-${kind}-csv`}><Download size={13} /> CSV</Button>
      <Button size="sm" variant="outline" className="gap-1" onClick={() => dl("excel")} data-testid={`export-${kind}-excel`}><FileSpreadsheet size={13} /> Excel</Button>
      <Button size="sm" variant="outline" className="gap-1" onClick={() => dl("pdf")} data-testid={`export-${kind}-pdf`}><FileType size={13} /> PDF</Button>
    </div>
  );
}

export default function Reports() {
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState("all");
  const [txns, setTxns] = useState([]);
  const [emps, setEmps] = useState([]);
  const [storeRows, setStoreRows] = useState([]);
  const [peak, setPeak] = useState([]);
  const [period, setPeriod] = useState("today");
  const [comparison, setComparison] = useState([]);
  const [cFrom, setCFrom] = useState("");
  const [cTo, setCTo] = useState("");
  const [leaders, setLeaders] = useState([]);
  const [lbPeriod, setLbPeriod] = useState("30");
  const [comparisonEnd, setComparisonEnd] = useState(null);
  const [hof, setHof] = useState([]);
  const [tpMonth, setTpMonth] = useState(new Date().toISOString().slice(0, 7));
  const [topPerf, setTopPerf] = useState(null);
  const [certEmail, setCertEmail] = useState("");
  const [sendingCert, setSendingCert] = useState(false);

  useEffect(() => {
    api.get(`/reports/top-performer?month=${tpMonth}`).then((r) => setTopPerf(r.data)).catch(() => {});
  }, [tpMonth]);

  const buildCertHtml = (w, label, company) => `
    <div style="font-family:Georgia,'Times New Roman',serif;max-width:720px;margin:40px auto;border:6px double #1a7a3c;padding:48px 40px;text-align:center;background:#fffdf5;color:#111">
      <div style="text-transform:uppercase;letter-spacing:6px;color:#c78a00;font-size:13px">${company}</div>
      <h1 style="font-size:34px;margin:14px 0 4px;color:#1a7a3c">Certificate of Excellence</h1>
      <div style="color:#777;font-size:14px;letter-spacing:1px">Top Performer &middot; ${label}</div>
      <div style="height:2px;background:#c78a00;width:90px;margin:24px auto"></div>
      <div style="font-size:15px;color:#333">This certificate is proudly presented to</div>
      <div style="font-size:40px;font-weight:bold;margin:10px 0 4px">${w.full_name}</div>
      <div style="text-transform:uppercase;letter-spacing:2px;color:#777;font-size:12px">${w.role.replace(/_/g, " ")} &middot; ${w.store}</div>
      <p style="font-size:15px;color:#444;margin:26px auto;max-width:540px;line-height:1.7">In recognition of outstanding service performance — achieving <b>${w.within_pct}% on-target</b> across <b>${w.handled}</b> packages handled, at an average handling time of <b>${fmtDuration(w.avg_seconds)}</b>.</p>
      <div style="margin-top:34px;color:#999;font-size:12px">Awarded by Trackboard &middot; ${new Date().toLocaleDateString()}</div>
    </div>`;

  const previewCert = () => {
    if (!topPerf?.winner) return;
    const w = window.open("", "_blank", "width=820,height=680");
    if (!w) { toast.error("Allow pop-ups to preview"); return; }
    w.document.write(`<!doctype html><html><head><title>Certificate — ${topPerf.winner.full_name}</title></head><body style="margin:0;background:#e9e9e9">${buildCertHtml(topPerf.winner, topPerf.month_label, "Trackboard")}<script>window.onload=function(){setTimeout(function(){window.print()},600)}</script></body></html>`);
    w.document.close();
  };

  const sendCert = async () => {
    if (!topPerf?.winner) return;
    setSendingCert(true);
    try {
      const body = { month: tpMonth, user_id: topPerf.winner.user_id };
      if (certEmail.trim()) body.recipient_email = certEmail.trim();
      const { data } = await api.post("/reports/certificate/send", body);
      toast.success(`Certificate emailed to ${data.recipient}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send certificate");
    } finally { setSendingCert(false); }
  };

  useEffect(() => { api.get("/reports/hall-of-fame").then((r) => setHof(r.data.rows)).catch(() => {}); }, []);

  useEffect(() => { api.get("/stores").then((r) => setStores(r.data.stores)).catch(() => {}); }, []);

  useEffect(() => {
    let url = "/reports/leaderboard";
    if (lbPeriod !== "all") {
      const d = new Date(); d.setDate(d.getDate() - Number(lbPeriod)); d.setHours(0, 0, 0, 0);
      url += `?date_from=${d.toISOString()}`;
    }
    api.get(url).then((r) => setLeaders(r.data.rows)).catch(() => {});
  }, [lbPeriod]);

  useEffect(() => {
    let url = `/reports/comparison?period=${period}`;
    if (period === "custom") {
      if (!cFrom || !cTo) return;
      url += `&date_from=${cFrom}T00:00:00&date_to=${cTo}T23:59:59`;
    }
    api.get(url).then((r) => { setComparison(r.data.rows); setComparisonEnd(r.data.end); }).catch(() => {});
  }, [period, cFrom, cTo]);

  useEffect(() => {
    const q = storeId !== "all" ? `?store_id=${storeId}` : "";
    api.get(`/reports/transactions${q}`).then((r) => setTxns(r.data.rows)).catch(() => {});
    api.get(`/reports/employees${q}`).then((r) => setEmps(r.data.rows)).catch(() => {});
    api.get(`/reports/stores`).then((r) => setStoreRows(r.data.rows)).catch(() => {});
    api.get(`/reports/peak-hours${q}`).then((r) => setPeak(r.data.rows)).catch(() => {});
  }, [storeId]);

  return (
    <DashboardLayout title="Reports" needsStore={false}>
      <div className="mb-4">
        <Select value={storeId} onValueChange={setStoreId}>
          <SelectTrigger className="w-64" data-testid="report-store-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stores</SelectItem>
            {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.store_name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="transactions">
        <TabsList className="mb-4">
          <TabsTrigger value="transactions" data-testid="tab-txn-report"><FileText size={14} className="mr-1" /> Transactions</TabsTrigger>
          <TabsTrigger value="employees" data-testid="tab-emp-report">Employees</TabsTrigger>
          <TabsTrigger value="stores" data-testid="tab-store-report">Stores</TabsTrigger>
          <TabsTrigger value="comparison" data-testid="tab-comparison-report"><Trophy size={14} className="mr-1" /> Comparison</TabsTrigger>
          <TabsTrigger value="leaderboard" data-testid="tab-leaderboard-report"><Crown size={14} className="mr-1" /> Leaderboard</TabsTrigger>
          <TabsTrigger value="hall" data-testid="tab-hall-report"><Award size={14} className="mr-1" /> Hall of Fame</TabsTrigger>
          <TabsTrigger value="peak" data-testid="tab-peak-report">Peak Hours</TabsTrigger>
        </TabsList>

        <TabsContent value="transactions">
          <div className="flex justify-end mb-3"><ExportButtons kind="transactions" storeId={storeId} /></div>
          <div className="bg-card border border-border overflow-x-auto max-h-[70vh]">
            <table className="w-full text-xs" data-testid="txn-report-table">
              <thead className="sticky top-0 bg-card"><tr className="text-left uppercase text-muted-foreground border-b border-border">
                <th className="px-2 py-2">Txn #</th><th className="px-2 py-2">KIN</th><th className="px-2 py-2">Store</th>
                <th className="px-2 py-2">Checked In</th><th className="px-2 py-2">WH Emp</th><th className="px-2 py-2">Prep</th>
                <th className="px-2 py-2">FD Emp</th><th className="px-2 py-2">Process</th><th className="px-2 py-2">Total Wait</th>
                <th className="px-2 py-2">Status</th><th className="px-2 py-2">Result</th></tr></thead>
              <tbody>
                {txns.map((r, i) => (
                  <tr key={i} className="border-b border-border">
                    <td className="px-2 py-2 font-mono-t">{r.transaction_number}</td>
                    <td className="px-2 py-2 font-mono-t">{r.kin_number}</td>
                    <td className="px-2 py-2">{r.store}</td>
                    <td className="px-2 py-2">{fmtDateTime(r.checked_in_at)}</td>
                    <td className="px-2 py-2">{r.warehouse_employee || "—"}</td>
                    <td className="px-2 py-2">{fmtDuration(r.warehouse_prep_seconds)}</td>
                    <td className="px-2 py-2">{r.frontdesk_employee || "—"}</td>
                    <td className="px-2 py-2">{fmtDuration(r.frontdesk_process_seconds)}</td>
                    <td className="px-2 py-2">{fmtDuration(r.total_wait_seconds)}</td>
                    <td className="px-2 py-2">{r.final_status}</td>
                    <td className="px-2 py-2"><span className={r.service_result === "Exceeded Target" ? "text-alert-red" : r.service_result === "Within Target" ? "text-alert-green" : ""}>{r.service_result}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="employees">
          <div className="flex justify-end mb-3"><ExportButtons kind="employees" storeId={storeId} /></div>
          <div className="bg-card border border-border overflow-x-auto">
            <table className="w-full text-sm" data-testid="emp-report-table">
              <thead><tr className="text-left uppercase text-xs text-muted-foreground border-b border-border">
                <th className="px-3 py-2">Employee</th><th className="px-3 py-2">Role</th><th className="px-3 py-2">Handled</th>
                <th className="px-3 py-2">Avg</th><th className="px-3 py-2">Fastest</th><th className="px-3 py-2">Slowest</th>
                <th className="px-3 py-2">Within %</th><th className="px-3 py-2">Alerts</th><th className="px-3 py-2">Ack'd</th></tr></thead>
              <tbody>
                {emps.map((r, i) => (
                  <tr key={i} className="border-b border-border">
                    <td className="px-3 py-2">{r.full_name} <span className="text-xs text-muted-foreground font-mono-t">{r.employee_number}</span></td>
                    <td className="px-3 py-2 text-xs uppercase">{r.role.replace(/_/g," ")}</td>
                    <td className="px-3 py-2">{r.handled}</td><td className="px-3 py-2">{fmtDuration(r.avg_seconds)}</td>
                    <td className="px-3 py-2">{fmtDuration(r.fastest_seconds)}</td><td className="px-3 py-2">{fmtDuration(r.slowest_seconds)}</td>
                    <td className="px-3 py-2">{r.within_pct}%</td><td className="px-3 py-2">{r.alerts}</td><td className="px-3 py-2">{r.alerts_acked}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="stores">
          <div className="flex justify-end mb-3"><ExportButtons kind="stores" storeId={storeId} /></div>
          <div className="bg-card border border-border overflow-x-auto">
            <table className="w-full text-sm" data-testid="store-report-table">
              <thead><tr className="text-left uppercase text-xs text-muted-foreground border-b border-border">
                <th className="px-3 py-2">Store</th><th className="px-3 py-2">Served</th><th className="px-3 py-2">Avg Accept</th>
                <th className="px-3 py-2">Avg Prep</th><th className="px-3 py-2">Avg Queue</th><th className="px-3 py-2">Avg Process</th>
                <th className="px-3 py-2">Avg Total</th><th className="px-3 py-2">Within %</th><th className="px-3 py-2">Busiest</th></tr></thead>
              <tbody>
                {storeRows.map((r, i) => (
                  <tr key={i} className="border-b border-border">
                    <td className="px-3 py-2 font-semibold">{r.store_name}</td><td className="px-3 py-2">{r.served}</td>
                    <td className="px-3 py-2">{fmtDuration(r.avg_warehouse_accept)}</td><td className="px-3 py-2">{fmtDuration(r.avg_warehouse_prep)}</td>
                    <td className="px-3 py-2">{fmtDuration(r.avg_frontdesk_queue)}</td><td className="px-3 py-2">{fmtDuration(r.avg_frontdesk_process)}</td>
                    <td className="px-3 py-2">{fmtDuration(r.avg_total_wait)}</td><td className="px-3 py-2">{r.within_pct}%</td><td className="px-3 py-2">{r.busiest_hour}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="comparison">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-48" data-testid="comparison-period"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="yesterday">Yesterday</SelectItem>
                  <SelectItem value="week">This Week</SelectItem>
                  <SelectItem value="last_week">Last Week</SelectItem>
                  <SelectItem value="month">This Month</SelectItem>
                  <SelectItem value="last_month">Last Month</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
              {period === "custom" && (
                <div className="flex items-center gap-2">
                  <Input type="date" className="h-9 w-40" value={cFrom} onChange={(e) => setCFrom(e.target.value)} data-testid="comparison-date-from" />
                  <span className="text-muted-foreground text-sm">to</span>
                  <Input type="date" className="h-9 w-40" value={cTo} onChange={(e) => setCTo(e.target.value)} data-testid="comparison-date-to" />
                </div>
              )}
            </div>
            <span className="text-xs text-muted-foreground uppercase tracking-wide">Ranked by on-target %, then average wait</span>
          </div>

          {/* Podium */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {comparison.filter((r) => r.served > 0).slice(0, 3).map((r, i) => {
              const medal = ["#F5C518", "#C0C0C0", "#CD7F32"][i];
              return (
                <div key={r.store_id} className="bg-card border border-border p-5 relative" data-testid={`podium-${i + 1}`}
                  style={{ borderTopColor: medal, borderTopWidth: 3 }}>
                  <div className="flex items-center justify-between">
                    <Medal size={22} style={{ color: medal }} />
                    <span className="font-display text-4xl font-extrabold" style={{ color: medal }}>#{r.rank}</span>
                  </div>
                  <div className="font-display text-2xl font-bold mt-2">{r.store_name}</div>
                  <div className="text-xs text-muted-foreground">{r.town}, {r.parish}</div>
                  <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                    <div><div className="font-display text-2xl font-bold text-alert-green">{r.within_pct}%</div><div className="text-[10px] uppercase text-muted-foreground">On Target</div></div>
                    <div><div className="font-display text-2xl font-bold">{r.served}</div><div className="text-[10px] uppercase text-muted-foreground">Served</div></div>
                    <div><div className="font-display text-2xl font-bold text-alert-amber">{fmtDuration(r.avg_total_wait)}</div><div className="text-[10px] uppercase text-muted-foreground">Avg Wait</div></div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-card border border-border overflow-x-auto">
              <table className="w-full text-sm" data-testid="comparison-table">
                <thead><tr className="text-left uppercase text-xs text-muted-foreground border-b border-border">
                  <th className="px-3 py-2">Rank</th><th className="px-3 py-2">Store</th><th className="px-3 py-2">Served</th>
                  <th className="px-3 py-2">On Target</th><th className="px-3 py-2">Trend</th><th className="px-3 py-2">7-Day</th><th className="px-3 py-2">Avg Wait</th><th className="px-3 py-2">Avg Prep</th>
                  <th className="px-3 py-2">Avg Process</th><th className="px-3 py-2">Longest</th></tr></thead>
                <tbody>
                  {comparison.map((r) => (
                    <tr key={r.store_id} className="border-b border-border hover:bg-secondary/40 transition-colors" data-testid={`comparison-row-${r.store_code}`}>
                      <td className="px-3 py-3 font-display text-xl font-bold">{r.rank ? `#${r.rank}` : "—"}</td>
                      <td className="px-3 py-3"><span className="font-semibold">{r.store_name}</span> <span className="font-mono-t text-xs text-muted-foreground">{r.store_code}</span></td>
                      <td className="px-3 py-3">{r.served}</td>
                      <td className="px-3 py-3"><span className={r.within_pct >= 80 ? "text-alert-green" : r.within_pct >= 50 ? "text-alert-amber" : "text-alert-red"}>{r.served ? `${r.within_pct}%` : "—"}</span></td>
                      <td className="px-3 py-3" data-testid={`trend-${r.store_code}`}>
                        {r.trend == null ? <span className="text-muted-foreground text-xs" title="Not enough prior-period data to compare">—</span> :
                          r.trend > 0 ? <span className="flex items-center gap-1 text-alert-green"><TrendingUp size={14} />+{r.trend}%</span> :
                          r.trend < 0 ? <span className="flex items-center gap-1 text-alert-red"><TrendingDown size={14} />{r.trend}%</span> :
                          <span className="flex items-center gap-1 text-muted-foreground"><Minus size={14} />0%</span>}
                      </td>
                      <td className="px-3 py-3" data-testid={`spark-${r.store_code}`}><Sparkline data={r.spark} endDate={comparisonEnd} /></td>
                      <td className="px-3 py-3">{fmtDuration(r.avg_total_wait)}</td>
                      <td className="px-3 py-3">{fmtDuration(r.avg_prep)}</td>
                      <td className="px-3 py-3">{fmtDuration(r.avg_process)}</td>
                      <td className="px-3 py-3">{fmtDuration(r.longest_wait)}</td>
                    </tr>
                  ))}
                  {comparison.every((r) => r.served === 0) && (
                    <tr><td colSpan={10} className="text-center text-muted-foreground py-8">No completed transactions in this period.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="bg-card border border-border p-5" data-testid="comparison-chart">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-3">Average Total Wait by Store</div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={comparison.map((r) => ({ name: r.store_code, minutes: Math.round(r.avg_total_wait / 60), within: r.within_pct }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
                  <XAxis dataKey="name" stroke="#666" fontSize={12} />
                  <YAxis stroke="#666" fontSize={12} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#121212", border: "1px solid #262626", color: "#fff" }} formatter={(v, n) => n === "minutes" ? [`${v} min`, "Avg Wait"] : [`${v}%`, "On Target"]} />
                  <Bar dataKey="minutes" fill="#F59E0B" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="leaderboard">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <Select value={lbPeriod} onValueChange={setLbPeriod}>
              <SelectTrigger className="w-44" data-testid="leaderboard-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="all">All time</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground uppercase tracking-wide">Ranked by on-target %, then volume</span>
          </div>

          {(() => {
            const star = leaders.filter((l) => l.streak >= 2).sort((a, b) => b.streak - a.streak)[0];
            if (!star) return null;
            return (
              <div className="bg-gradient-to-r from-[#1a1200] to-card border border-alert-amber/40 border-l-4 border-l-alert-amber p-5 mb-4 flex items-center gap-4" data-testid="employee-of-week">
                <div className="w-14 h-14 rounded-full bg-alert-amber/15 flex items-center justify-center shrink-0">
                  <Flame size={28} className="text-alert-amber" />
                </div>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-widest text-alert-amber">Employee of the Week</div>
                  <div className="font-display text-3xl font-extrabold leading-none mt-1">{star.full_name}</div>
                  <div className="text-sm text-muted-foreground">{star.store} · {star.within_pct}% on-target · {star.handled} handled</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-5xl font-extrabold text-alert-amber leading-none">{star.streak}</div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">day streak</div>
                </div>
              </div>
            );
          })()}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {leaders.slice(0, 3).map((r, i) => {
              const medal = ["#F5C518", "#C0C0C0", "#CD7F32"][i];
              return (
                <div key={r.user_id} className="bg-card border border-border p-5" data-testid={`leader-podium-${i + 1}`}
                  style={{ borderTopColor: medal, borderTopWidth: 3 }}>
                  <div className="flex items-center justify-between">
                    <Crown size={22} style={{ color: medal }} />
                    <span className="font-display text-4xl font-extrabold" style={{ color: medal }}>#{r.rank}</span>
                  </div>
                  <div className="font-display text-2xl font-bold mt-2">{r.full_name}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">{r.role.replace(/_/g, " ")} · {r.store}</div>
                  <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                    <div><div className="font-display text-2xl font-bold text-alert-green">{r.within_pct}%</div><div className="text-[10px] uppercase text-muted-foreground">On Target</div></div>
                    <div><div className="font-display text-2xl font-bold">{r.handled}</div><div className="text-[10px] uppercase text-muted-foreground">Handled</div></div>
                    <div><div className="font-display text-2xl font-bold text-alert-amber">{fmtDuration(r.avg_seconds)}</div><div className="text-[10px] uppercase text-muted-foreground">Avg</div></div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-card border border-border overflow-x-auto">
            <table className="w-full text-sm" data-testid="leaderboard-table">
              <thead><tr className="text-left uppercase text-xs text-muted-foreground border-b border-border">
                <th className="px-3 py-2">Rank</th><th className="px-3 py-2">Employee</th><th className="px-3 py-2">Role</th>
                <th className="px-3 py-2">Store</th><th className="px-3 py-2">Handled</th><th className="px-3 py-2">On Target</th><th className="px-3 py-2">Avg Time</th></tr></thead>
              <tbody>
                {leaders.map((r) => (
                  <tr key={r.user_id} className="border-b border-border hover:bg-secondary/40 transition-colors" data-testid={`leader-row-${r.user_id}`}>
                    <td className="px-3 py-3 font-display text-xl font-bold">#{r.rank}</td>
                    <td className="px-3 py-3 font-semibold"><span className="flex items-center gap-2">{r.full_name}{r.streak >= 2 && <span className="flex items-center gap-0.5 text-xs text-alert-red" title={`${r.streak}-day 100% on-target streak`}><Flame size={12} />{r.streak}</span>}</span></td>
                    <td className="px-3 py-3 text-xs uppercase">{r.role.replace(/_/g, " ")}</td>
                    <td className="px-3 py-3">{r.store}</td>
                    <td className="px-3 py-3">{r.handled}</td>
                    <td className="px-3 py-3"><span className={r.within_pct >= 80 ? "text-alert-green" : r.within_pct >= 50 ? "text-alert-amber" : "text-alert-red"}>{r.within_pct}%</span></td>
                    <td className="px-3 py-3">{fmtDuration(r.avg_seconds)}</td>
                  </tr>
                ))}
                {leaders.length === 0 && <tr><td colSpan={7} className="text-center text-muted-foreground py-8">No completed transactions in this period.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="hall">
          <div className="flex items-center justify-between mb-4">
            <span className="font-display text-xl font-bold flex items-center gap-2"><Award size={20} className="text-alert-amber" /> Streak Hall of Fame</span>
            <span className="text-xs text-muted-foreground uppercase tracking-wide">Longest 100% on-target daily streaks ever achieved</span>
          </div>

          <div className="bg-gradient-to-r from-[#0f1a10] to-card border border-alert-green/40 border-l-4 border-l-alert-green p-5 mb-5" data-testid="top-performer-card">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
              <span className="text-xs uppercase tracking-widest text-alert-green flex items-center gap-2"><Trophy size={15} /> Top Performer of the Month</span>
              <Input type="month" className="h-9 w-44" value={tpMonth} onChange={(e) => setTpMonth(e.target.value)} data-testid="top-performer-month" />
            </div>
            {topPerf?.winner ? (
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-alert-green/15 flex items-center justify-center shrink-0"><Trophy size={26} className="text-alert-green" /></div>
                  <div>
                    <div className="font-display text-3xl font-extrabold leading-none">{topPerf.winner.full_name}</div>
                    <div className="text-sm text-muted-foreground">{topPerf.winner.store} · {topPerf.month_label} · {topPerf.winner.within_pct}% on-target · {topPerf.winner.handled} handled</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Input type="email" placeholder="Override recipient (optional)" className="h-9 w-60" value={certEmail} onChange={(e) => setCertEmail(e.target.value)} data-testid="cert-recipient" />
                  <Button size="sm" variant="outline" className="gap-1" onClick={previewCert} data-testid="cert-preview-btn"><FileType size={14} /> Preview</Button>
                  <Button size="sm" className="gap-1" onClick={sendCert} disabled={sendingCert} data-testid="cert-send-btn"><Award size={14} /> {sendingCert ? "Sending…" : "Email Certificate"}</Button>
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground py-2">No performance data for {topPerf?.month_label || "this month"} yet.</div>
            )}
          </div>

          {hof.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {hof.slice(0, 3).map((r, i) => {
                const medal = ["#F5C518", "#C0C0C0", "#CD7F32"][i];
                return (
                  <div key={r.user_id} className="bg-card border border-border p-5" data-testid={`hof-podium-${i + 1}`}
                    style={{ borderTopColor: medal, borderTopWidth: 3 }}>
                    <div className="flex items-center justify-between">
                      <Award size={22} style={{ color: medal }} />
                      <span className="font-display text-4xl font-extrabold" style={{ color: medal }}>#{r.rank}</span>
                    </div>
                    <div className="font-display text-2xl font-bold mt-2">{r.full_name}</div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wide">{r.role.replace(/_/g, " ")} · {r.store}</div>
                    <div className="flex items-end gap-2 mt-4">
                      <Flame size={26} className="text-alert-red mb-1" />
                      <span className="font-display text-5xl font-extrabold text-alert-red leading-none">{r.best_streak}</span>
                      <span className="text-[10px] uppercase text-muted-foreground mb-1.5">day streak</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-2">{fmtDateTime(r.streak_start)?.split(",")[0]} → {fmtDateTime(r.streak_end)?.split(",")[0]}</div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="bg-card border border-border overflow-x-auto">
            <table className="w-full text-sm" data-testid="hall-of-fame-table">
              <thead><tr className="text-left uppercase text-xs text-muted-foreground border-b border-border">
                <th className="px-3 py-2">Rank</th><th className="px-3 py-2">Employee</th><th className="px-3 py-2">Role</th>
                <th className="px-3 py-2">Store</th><th className="px-3 py-2">Best Streak</th><th className="px-3 py-2">Period</th><th className="px-3 py-2">Active Days</th></tr></thead>
              <tbody>
                {hof.map((r) => (
                  <tr key={r.user_id} className="border-b border-border hover:bg-secondary/40 transition-colors" data-testid={`hof-row-${r.user_id}`}>
                    <td className="px-3 py-3 font-display text-xl font-bold">#{r.rank}</td>
                    <td className="px-3 py-3 font-semibold">{r.full_name}</td>
                    <td className="px-3 py-3 text-xs uppercase">{r.role.replace(/_/g, " ")}</td>
                    <td className="px-3 py-3">{r.store}</td>
                    <td className="px-3 py-3"><span className="flex items-center gap-1 text-alert-red font-semibold"><Flame size={14} />{r.best_streak} days</span></td>
                    <td className="px-3 py-3 text-xs text-muted-foreground">{fmtDateTime(r.streak_start)?.split(",")[0]} → {fmtDateTime(r.streak_end)?.split(",")[0]}</td>
                    <td className="px-3 py-3">{r.active_days}</td>
                  </tr>
                ))}
                {hof.length === 0 && <tr><td colSpan={7} className="text-center text-muted-foreground py-8">No perfect on-target streaks recorded yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="peak">
          <div className="bg-card border border-border p-5" data-testid="peak-chart">
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={peak}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
                <XAxis dataKey="hour" stroke="#666" fontSize={12} />
                <YAxis stroke="#666" fontSize={12} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#121212", border: "1px solid #262626", color: "#fff" }} />
                <Bar dataKey="count" fill="#FF3B30" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}


